import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Phone, Globe, Twitter, MapPin, Building2, 
  UserPlus, UserCheck, Calendar, Newspaper, Bell, Share2, ExternalLink,
  PoundSterling, TrendingUp, Users, Home, Car, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { getPartyConfig } from '@/lib/theme';
import SEO from '@/components/SEO';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const PoliticianProfilePage = () => {
  const { slug } = useParams();
  const { token, user } = useAuth();
  
  const [politician, setPolitician] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isWatching, setIsWatching] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);
  const [expenses, setExpenses] = useState(null);
  const [expensesLoading, setExpensesLoading] = useState(false);

  useEffect(() => {
    const fetchPolitician = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/politician/${slug}`);
        setPolitician(response.data);
        
        // Check if watching
        if (token) {
          try {
            const watchCheck = await axios.get(
              `${API}/watchlist/check/${encodeURIComponent(response.data.name)}`,
              { headers: { Authorization: `Bearer ${token}` }}
            );
            setIsWatching(watchCheck.data.watching);
          } catch (e) {}
        }
        
        // Fetch expenses for MPs
        if (response.data.politician_type === 'MP') {
          setExpensesLoading(true);
          try {
            const expensesResponse = await axios.get(
              `${API}/mp-expenses/${encodeURIComponent(response.data.name)}`
            );
            setExpenses(expensesResponse.data);
          } catch (e) {
            console.log('No expenses data available');
          } finally {
            setExpensesLoading(false);
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Politician not found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPolitician();
  }, [slug, token]);

  const handleWatchToggle = async () => {
    if (!token) {
      toast.error('Please log in to add to watchlist');
      return;
    }
    
    setWatchLoading(true);
    try {
      if (isWatching) {
        await axios.delete(
          `${API}/watchlist/${encodeURIComponent(politician.name)}`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setIsWatching(false);
        toast.success('Removed from watchlist');
      } else {
        await axios.post(`${API}/watchlist/add`, {
          politician_name: politician.name,
          politician_type: politician.politician_type,
          constituency: politician.constituency,
          council: politician.council,
          party: politician.party
        }, { headers: { Authorization: `Bearer ${token}` }});
        setIsWatching(true);
        toast.success('Added to watchlist! You\'ll get alerts when they\'re in the news.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update watchlist');
    } finally {
      setWatchLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${politician.name} - The Watchtower`,
          text: `Check out ${politician.name}'s political profile and news coverage`,
          url
        });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-4 text-slate-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !politician) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Politician Not Found</h1>
          <p className="text-slate-500 mb-4">{error}</p>
          <Link to="/politicians">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Search
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const partyConfig = getPartyConfig(politician.party);
  const partyLabel = partyConfig.label || politician.party;
  const location = politician.constituency || politician.council;

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="politician-profile">
      <SEO 
        title={`${politician.name} - ${partyLabel} ${politician.politician_type}`}
        description={`Latest news and information about ${politician.name}, ${partyLabel} ${politician.politician_type} for ${location}. Contact details, voting record, and political coverage.`}
        keywords={`${politician.name}, ${partyLabel} ${politician.politician_type}, ${location} MP, ${politician.name} news, ${politician.name} scandals, UK politician`}
        type="profile"
        politician={politician}
      />
      
      {/* Header */}
      <div 
        className="py-12 px-4"
        style={{ 
          background: `linear-gradient(135deg, ${partyConfig.color}20 0%, ${partyConfig.color}10 100%)`
        }}
      >
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/politicians" 
            className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Search
          </Link>
          
          <div className="flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left">
            {/* Photo or Placeholder */}
            <div 
              className="w-32 h-32 rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-lg mx-auto md:mx-0"
              style={{ backgroundColor: partyConfig.color }}
            >
              {politician.photo_url ? (
                <img src={politician.photo_url} alt={politician.name} className="w-full h-full rounded-2xl object-cover" />
              ) : (
                politician.name.split(' ').map(n => n[0]).join('').slice(0, 2)
              )}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                <span 
                  className="px-3 py-1 rounded-full text-sm font-bold"
                  style={{ backgroundColor: partyConfig.color, color: 'white' }}
                >
                  {partyConfig.label}
                </span>
                <span className="text-slate-500">{politician.politician_type}</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-2">
                {politician.name}
              </h1>
              
              <div className="flex items-center justify-center md:justify-start gap-2 text-slate-600">
                <MapPin className="h-4 w-4" />
                <span>{politician.constituency || politician.council}</span>
                {politician.ward && <span className="text-slate-400">• {politician.ward}</span>}
              </div>
              
              {/* Actions */}
              <div className="flex flex-wrap gap-3 mt-4 justify-center md:justify-start">
                <Button 
                  onClick={handleWatchToggle}
                  disabled={watchLoading}
                  variant={isWatching ? 'secondary' : 'default'}
                  className={isWatching ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : ''}
                  data-testid="watch-button"
                >
                  {isWatching ? (
                    <>
                      <UserCheck className="h-4 w-4 mr-2" />
                      Watching
                    </>
                  ) : (
                    <>
                      <Bell className="h-4 w-4 mr-2" />
                      Watch for News
                    </>
                  )}
                </Button>
                
                <Button variant="outline" onClick={handleShare}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-8">
            {/* News Articles */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Newspaper className="h-5 w-5 text-blue-500" />
                Recent News Coverage
                <span className="text-sm font-normal text-slate-500">
                  ({politician.article_count || 0} articles)
                </span>
              </h2>
              
              {politician.articles && politician.articles.length > 0 ? (
                <div className="space-y-4">
                  {politician.articles.map((article, idx) => (
                    <Link 
                      key={idx}
                      to={`/article/${article.id}`}
                      className="block p-4 rounded-xl hover:bg-slate-50 transition-colors border border-slate-100"
                    >
                      <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2">
                        {article.title}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span>{article.source}</span>
                        <span>•</span>
                        <span>{new Date(article.published_date).toLocaleDateString('en-GB')}</span>
                      </div>
                    </Link>
                  ))}
                  
                  {politician.article_count > 20 && (
                    <p className="text-center text-slate-500 text-sm pt-2">
                      Showing 20 of {politician.article_count} articles
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Newspaper className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No news articles found for this politician yet.</p>
                  <p className="text-sm mt-2">Add them to your watchlist to get notified when they appear in the news.</p>
                </div>
              )}
            </div>
            
            {/* MP Expenses Section - Only for MPs */}
            {politician.politician_type === 'MP' && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200" data-testid="mp-expenses-section">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <PoundSterling className="h-5 w-5 text-emerald-500" />
                  Expenses (2024-25)
                  <Link 
                    to="/mp-expenses" 
                    className="ml-auto text-sm font-normal text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    View Leaderboard <ExternalLink className="h-3 w-3" />
                  </Link>
                </h2>
                
                {expensesLoading ? (
                  <div className="animate-pulse space-y-3">
                    <div className="h-20 bg-slate-100 rounded-xl"></div>
                    <div className="h-32 bg-slate-100 rounded-xl"></div>
                  </div>
                ) : expenses?.total_summary ? (
                  <div className="space-y-4">
                    {/* Total Spend */}
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-4 text-white">
                      <div className="text-3xl font-black">{formatCurrency(expenses.total_summary.total)}</div>
                      <div className="text-sm opacity-80">Total Expenses Claimed</div>
                    </div>
                    
                    {/* Breakdown */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-50 rounded-xl p-3">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <Users className="h-3 w-3" />
                          Staffing
                        </div>
                        <div className="font-bold text-slate-900">{formatCurrency(expenses.total_summary.staffing_spend)}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <Briefcase className="h-3 w-3" />
                          Office
                        </div>
                        <div className="font-bold text-slate-900">{formatCurrency(expenses.total_summary.office_spend)}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <Home className="h-3 w-3" />
                          Accommodation
                        </div>
                        <div className="font-bold text-slate-900">{formatCurrency(expenses.total_summary.accommodation_spend)}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <Car className="h-3 w-3" />
                          Travel
                        </div>
                        <div className="font-bold text-slate-900">{formatCurrency(expenses.total_summary.travel_spend)}</div>
                      </div>
                    </div>
                    
                    {/* Recent Claims */}
                    {expenses.expenses && expenses.expenses.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2 text-sm">Recent Claims ({expenses.total_claims} total)</h3>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {expenses.expenses.slice(0, 10).map((exp, idx) => (
                            <div key={idx} className="flex justify-between items-start text-sm border-b border-slate-100 pb-2">
                              <div className="flex-1 min-w-0 pr-2">
                                <div className="font-medium text-slate-900">{exp.category}</div>
                                <div className="text-xs text-slate-500">{exp.cost_type}</div>
                                {exp.details && (
                                  <div className="text-xs text-slate-400 truncate" title={exp.details}>{exp.details}</div>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="font-bold text-slate-900">{formatCurrency(exp.amount_paid)}</div>
                                <div className="text-xs text-slate-500">{exp.date}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                        {expenses.total_claims > 10 && (
                          <p className="text-xs text-slate-400 text-center mt-2">
                            Showing 10 of {expenses.total_claims} claims
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* IPSA Link */}
                    <div className="pt-2 border-t border-slate-200">
                      {expenses.ipsa_url ? (
                        <a 
                          href={expenses.ipsa_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium py-2 px-4 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                          View Full Details on IPSA Official Website
                        </a>
                      ) : (
                        <p className="text-xs text-slate-400 text-center">
                          Source: IPSA (Independent Parliamentary Standards Authority)
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <PoundSterling className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No expenses data available for this MP.</p>
                    <p className="text-xs mt-1">Data may not be available for newly elected MPs.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-lg font-bold mb-4">Contact Information</h2>
              
              <div className="space-y-3">
                {politician.email && (
                  <a 
                    href={`mailto:${politician.email}`}
                    className="flex items-center gap-3 text-slate-600 hover:text-blue-600"
                  >
                    <Mail className="h-4 w-4" />
                    <span className="text-sm truncate">{politician.email}</span>
                  </a>
                )}
                
                {politician.phone && (
                  <a 
                    href={`tel:${politician.phone}`}
                    className="flex items-center gap-3 text-slate-600 hover:text-blue-600"
                  >
                    <Phone className="h-4 w-4" />
                    <span className="text-sm">{politician.phone}</span>
                  </a>
                )}
                
                {politician.website && (
                  <a 
                    href={politician.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-slate-600 hover:text-blue-600"
                  >
                    <Globe className="h-4 w-4" />
                    <span className="text-sm">Website</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                
                {politician.twitter && (
                  <a 
                    href={`https://twitter.com/${politician.twitter.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-slate-600 hover:text-blue-600"
                  >
                    <Twitter className="h-4 w-4" />
                    <span className="text-sm">{politician.twitter}</span>
                  </a>
                )}
                
                {!politician.email && !politician.phone && !politician.website && !politician.twitter && (
                  <div className="text-center">
                    {politician.politician_type === 'Councillor' && politician.council ? (
                      <>
                        <p className="text-sm text-slate-500 mb-3">Contact via council website</p>
                        <a 
                          href={`https://www.google.com/search?q=${encodeURIComponent(politician.council + ' council councillors contact')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700"
                        >
                          <Globe className="h-4 w-4" />
                          Find {politician.council} Council
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </>
                    ) : (
                      <p className="text-sm text-slate-400">No contact information available</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-lg font-bold mb-4">At a Glance</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Type</span>
                  <span className="font-medium">{politician.politician_type}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Party</span>
                  <span 
                    className="font-medium px-2 py-0.5 rounded"
                    style={{ backgroundColor: partyConfig.color + '20', color: partyConfig.color }}
                  >
                    {partyConfig.label}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Area</span>
                  <span className="font-medium text-right text-sm">
                    {politician.constituency || politician.council}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">News Articles</span>
                  <span className="font-medium">{politician.article_count || 0}</span>
                </div>
              </div>
            </div>

            {/* Watch CTA */}
            {!isWatching && (
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
                <Bell className="h-8 w-8 mb-3" />
                <h3 className="font-bold text-lg mb-2">Get Alerts</h3>
                <p className="text-blue-100 text-sm mb-4">
                  Add {politician.name} to your watchlist and get notified whenever they appear in the news.
                </p>
                <Button 
                  onClick={handleWatchToggle}
                  className="w-full bg-white text-blue-600 hover:bg-blue-50"
                >
                  <Bell className="h-4 w-4 mr-2" />
                  Watch for News
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoliticianProfilePage;
